import { useState } from 'react';

const useCustomFieldValues = (customFieldDefinitions, existingValues) => {
  const initValues = (existingValues) => {
    return (customFieldDefinitions || []).map((def) => {
      // allow_multiple association fields keep one state entry per field: value is
      // the react-select options array, existingRowIds maps value-id -> cfv row id
      // so buildCustomFieldAttributes can expand back into per-row nested attributes.
      if (def.type === 'CustomFields::Association' && def.allow_multiple) {
        const rows = (existingValues || []).filter((v) => v.custom_field_id === def.id);
        const withValue = rows.filter((r) => r.value);
        return {
          custom_field_id: def.id,
          type: 'CustomFieldValues::Association',
          custom_field: def,
          value: withValue.map((r) => ({ value: String(r.value), label: r.association_label || String(r.value) })),
          // If two rows share a value (no backend dedupe), the last id wins here;
          // the extra row is cleaned up on the next save via resetCustomFieldValues.
          existingRowIds: withValue.reduce((acc, r) => ({ ...acc, [String(r.value)]: r.id }), {}),
          // Blank rows left behind by the single-value editor are invisible in a
          // multi select; destroy them so they can't block disabling allow_multiple.
          blankRowIds: rows.filter((r) => !r.value && r.id).map((r) => r.id),
        };
      }
      const existing = (existingValues || []).find((v) => v.custom_field_id === def.id);
      if (existing) return { ...existing };
      return { custom_field_id: def.id, type: def.type.replace('CustomFields::', 'CustomFieldValues::'), value: def.type === 'CustomFields::Address' ? {} : '', custom_field: def };
    });
  };

  const [customFieldValues, setCustomFieldValues] = useState(() => initValues(existingValues));

  // Modals stay mounted across open/close, so hook state (including the row ids
  // multi-association fields submit) goes stale after a save. Call this from the
  // modal's isOpen effect with the record's current custom_field_values.
  const resetCustomFieldValues = (freshExistingValues) => {
    setCustomFieldValues(initValues(freshExistingValues));
  };

  // After a save, refresh multi-association row ids from the response without
  // touching in-progress edits (autosave modals can save mid-session); otherwise
  // the next save re-sends rows id-less and duplicates them.
  const syncCustomFieldRowIds = (savedValues) => {
    setCustomFieldValues((prev) => prev.map((cfv) => {
      if (!(cfv.type === 'CustomFieldValues::Association' && cfv.custom_field?.allow_multiple)) return cfv;
      const rows = (savedValues || []).filter((v) => v.custom_field_id === cfv.custom_field_id);
      const withValue = rows.filter((r) => r.value);
      return {
        ...cfv,
        existingRowIds: withValue.reduce((acc, r) => ({ ...acc, [String(r.value)]: r.id }), {}),
        blankRowIds: rows.filter((r) => !r.value && r.id).map((r) => r.id),
      };
    }));
  };

  const handleCustomFieldChange = (customFieldId, newValue, updatedCfv) => {
    setCustomFieldValues((prev) =>
      prev.map((cfv) => {
        if (cfv.custom_field_id !== customFieldId) return cfv;
        const updated = { ...cfv, value: newValue };
        if (updatedCfv) {
          if (updatedCfv.uploaded_file !== undefined) updated.uploaded_file = updatedCfv.uploaded_file;
          if (updatedCfv.uploaded_file_name !== undefined) updated.uploaded_file_name = updatedCfv.uploaded_file_name;
          if (updatedCfv.association_label !== undefined) updated.association_label = updatedCfv.association_label;
        }
        return updated;
      })
    );
  };

  const buildCustomFieldAttributes = () => {
    return customFieldValues.flatMap((cfv) => {
      // Expand a multi-association entry into one row per selected record:
      // existing rows keep their id, removed rows get _destroy, new ones are id-less.
      if (cfv.type === 'CustomFieldValues::Association' && cfv.custom_field?.allow_multiple) {
        const selected = Array.isArray(cfv.value) ? cfv.value : [];
        const existingRowIds = cfv.existingRowIds || {};
        const selectedIds = new Set(selected.map((o) => String(o.value)));
        const rows = selected.map((o) => {
          const row = { custom_field_id: cfv.custom_field_id, type: cfv.type, value: String(o.value) };
          if (existingRowIds[String(o.value)]) row.id = existingRowIds[String(o.value)];
          return row;
        });
        Object.entries(existingRowIds).forEach(([val, id]) => {
          if (!selectedIds.has(val)) rows.push({ id, _destroy: true });
        });
        (cfv.blankRowIds || []).forEach((id) => rows.push({ id, _destroy: true }));
        return rows;
      }
      const base = {
        custom_field_id: cfv.custom_field_id,
        type: cfv.type,
      };
      if (cfv.id) base.id = cfv.id;
      if (cfv.type === 'CustomFieldValues::Address') {
        const addr = cfv.value || {};
        return { ...base, address1: addr.address1 || '', address2: addr.address2 || '', city: addr.city || '', state: addr.state || '', zip: addr.zip || '', country: addr.country || '' };
      }
      if (cfv.type === 'CustomFieldValues::Phone') {
        return { ...base, value: (cfv.value || '').replace(/\D/g, '') };
      }
      if (cfv.type === 'CustomFieldValues::File') {
        const attrs = { ...base, value: cfv.value || '' };
        if (cfv.uploaded_file) attrs.uploaded_file = cfv.uploaded_file;
        return attrs;
      }
      return { ...base, value: cfv.value || '' };
    });
  };

  return { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes, resetCustomFieldValues, syncCustomFieldRowIds };
};

export default useCustomFieldValues;
